/**
 * A room's kind, resolved from the most authoritative source available.
 *
 * RoomPlan reports a room's category in `sections[].label` — that is the
 * field the scan populates and the field `RoomInfoPanel` edits. A room may
 * carry several labelled sections; an open-plan great room genuinely is a
 * kitchen AND a living room AND a dining room, so `resolveRoomKind` returns
 * the full set and `primaryRoomKind` collapses it when a caller needs one.
 *
 * Named to match `resolveObjectType`: this resolves a precedence chain
 * rather than inferring. Only the last step — matching substrings in a
 * free-text room name — is a guess, and it exists for rooms scanned before
 * sections were captured.
 *
 * The chain used to have a middle step reading the legacy room-level
 * `category` field (iOS scan intent before it was folded into `type`).
 * That alias was removed 2026-08-22 once the category→type migration had
 * run on every env (dev/staging/prod all verified at 0 stragglers) — see
 * `claude-docs/2026-08-21-room-type-standardization.md` Decision 2. The
 * `category` field itself is untouched; only this read was retired.
 */

// The ONE priority order in this file. `primaryRoomKind` returns the first of
// these present, and `matchKind` walks the patterns in this order too, so a
// room resolves the same way whether its kinds came from several section
// labels or from one string naming several ("Kitchen and Bath" → kitchen).
//
// bathroom outranks bedroom so a primary suite labelled `bedroom + bathroom`
// resolves to bathroom — that is what makes the Design Center offer its
// fixture categories there, which is the higher-traffic use. 4 rooms in dev
// carry that pair.
const { roomDisplayName } = require('./roomDisplayName');

const ROOM_KINDS = [
  'kitchen',
  'livingroom',
  'bathroom',
  'bedroom',
  'diningroom',
];

// Section labels and name fragments that map onto a kind. RoomPlan emits
// `livingroom`/`diningroom` unspaced; users type "living room".
//
// Fragments are matched as substrings, so every one must be long enough not
// to appear inside an unrelated word. 'den' is deliberately absent: RoomPlan's
// own `unidentified` label contains it (uni-DEN-tified), and 312 rooms in dev
// carry that label — they must resolve to 'other', not to a living room.
// Keyed by kind rather than written as an ordered list: the order that
// matters is ROOM_KINDS', and a second hand-ordered list would just drift
// from it (it had).
const KIND_PATTERNS = {
  bathroom: ['bath', 'powder', 'ensuite'],
  kitchen: ['kitchen'],
  // Bare 'great' (not just 'great room'): CV OCR emits the bare word.
  livingroom: ['living', 'great', 'family room', 'lounge'],
  diningroom: ['dining'],
  // 'dorm room' catches RoomPlan's 'Dorm Room' (247 rooms in dev, the second
  // most common name). They hold a bed, so first person should face it rather
  // than an arbitrary wall. Deliberately the full phrase, not 'dorm': that
  // shorter fragment also matches 'dormer', the same substring trap that once
  // made 'den' match 'unidentified'. A bare 'Dorm A' style name would need
  // adding here explicitly.
  bedroom: ['bedroom', 'bdrm', 'dorm room'],
};

/**
 * The room kind a piece of free text names, or `null`. Exported as
 * `matchRoomKind` for callers that need to classify ONE label rather
 * than a whole room — e.g. picking which of an open-plan room's
 * sections matches the purpose it is being offered for.
 */
const matchKind = (text) => {
  const lower = String(text ?? '').toLowerCase();
  if (!lower) return null;
  for (const kind of ROOM_KINDS) {
    const fragments = KIND_PATTERNS[kind];
    if (fragments && fragments.some((f) => lower.includes(f))) return kind;
  }
  return null;
};

/**
 * The kinds this room DECLARES — step 1 of the chain below. Returns
 * `null` when nothing declares one, which is what separates "declared
 * something else" from "said nothing at all".
 *
 * A free-text `name` is deliberately NOT a declaration: it is the guess
 * step, and callers that act on a room (rather than describe it) must not
 * act on a guess. See `resolveDeclaredRoomKind` for the exported form.
 */
const declaredKinds = (room) => {
  // 1. Explicit user declaration (web edit, or iOS scan intent since PR A).
  const fromType = matchKind(room?.type);
  if (fromType) return new Set([fromType]);

  // 2. RoomPlan section labels — the canonical source.
  const labelled = (room?.sections ?? [])
    .map((s) => matchKind(s?.label))
    .filter(Boolean);
  if (labelled.length) return new Set(labelled);

  return null;
};

/**
 * Every kind this room is. Returns `Set(['other'])` when nothing identifies
 * it — never an empty set, so callers can always read a kind.
 */
const resolveRoomKind = (room) => {
  const declared = declaredKinds(room);
  if (declared) return declared;

  // 3. Free-text room name, for scans predating sections.
  const fromName = matchKind(room?.name);
  if (fromName) return new Set([fromName]);

  return new Set(['other']);
};

/**
 * Like `resolveRoomKind`, but stops before the free-text name guess.
 *
 * Use this wherever the kind DRIVES A BUILD rather than a label: the
 * kitchen layout generator, cabinet fill, anything that writes objects
 * into a room. A room called "Kitchen B" that carries no `type` and no
 * labelled section is a name and nothing more — enough to title a card,
 * not enough to stamp cabinets into. Callers that only
 * DISPLAY or classify (room chips, Design Center categories, door-swing
 * priority) should keep using `resolveRoomKind` and its name fallback.
 */
const resolveDeclaredRoomKind = (room) =>
  declaredKinds(room) ?? new Set(['other']);

/** The single highest-priority kind, for callers that need exactly one. */
const primaryRoomKind = (room) => {
  const kinds = resolveRoomKind(room);
  for (const kind of ROOM_KINDS) {
    if (kinds.has(kind)) return kind;
  }
  return 'other';
};

// each kind as a homeowner says it
const KIND_WORDS = Object.freeze({
  kitchen: 'Kitchen',
  livingroom: 'Living room',
  bathroom: 'Bathroom',
  bedroom: 'Bedroom',
  diningroom: 'Dining room',
});

/** A kind in words ('livingroom' → "Living room"); null for 'other'. */
const roomKindWords = (kind) => KIND_WORDS[kind] ?? null;

/**
 * A room's name as a homeowner reads it (a customer's estimate, #36): its
 * own, the way people write it (`roomDisplayName`: the scan's `livingRoom`
 * is "Living room", a person's "Main Suite" stays), else its kind in words,
 * else null — never its id, which is what a room the scan left unnamed is
 * otherwise called. Words only: nothing matches on it.
 */
const customerRoomName = (room) =>
  (typeof room?.name === 'string' ? roomDisplayName(room.name) : null) ??
  KIND_WORDS[primaryRoomKind(room)] ??
  null;

module.exports = {
  ROOM_KINDS,
  customerRoomName,
  roomKindWords,
  matchRoomKind: matchKind,
  resolveRoomKind,
  resolveDeclaredRoomKind,
  primaryRoomKind,
};
